import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Receipt, MagnifyingGlass, FilePdf, WhatsappLogo, ShareNetwork, PencilSimple, X, Minus, Trash, Warning, Eye } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export function POSSalesHistory() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [docFilter, setDocFilter] = useState("");
  const [editingSale, setEditingSale] = useState(null);
  const [viewingSale, setViewingSale] = useState(null);
  const [deletingSale, setDeletingSale] = useState(null);

  const fetchSales = () => {
    setLoading(true);
    axios.get(`${API}/pos/sales?limit=200`, { headers })
      .then(r => setSales(r.data))
      .catch(() => toast.error("Error al cargar ventas"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSales(); }, [token]);

  const isOperator = ["admin", "auxiliar"].includes(user?.role);
  const isAdmin = user?.role === "admin";

  const handleDeleteSale = async (sale) => {
    setDeletingSale(sale.id);
    try {
      await axios.delete(`${API}/pos/sales/${sale.id}`, { headers });
      toast.success(`Venta ${sale.folio} eliminada`);
      setSales(prev => prev.filter(s => s.id !== sale.id));
      if (viewingSale?.id === sale.id) setViewingSale(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
    finally { setDeletingSale(null); }
  };

  const filtered = sales.filter(s => {
    const matchSearch = !search.trim() || (s.folio || "").toLowerCase().includes(search.toLowerCase()) || (s.customer_name || "").toLowerCase().includes(search.toLowerCase());
    const matchType = !docFilter || s.doc_type === docFilter;
    return matchSearch && matchType;
  });

  const totalAmount = filtered.reduce((s, v) => s + (v.total || 0), 0);

  const handleDownloadPdf = async (saleId) => {
    try {
      const res = await axios.get(`${API}/pos/sales/${saleId}/pdf`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `venta_${saleId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al generar PDF"); }
  };

  const handleShare = async (sale) => {
    try {
      const res = await axios.get(`${API}/pos/sales/${sale.id}/share`, { headers });
      window.open(res.data.whatsapp_url, "_blank");
    } catch { toast.error("Error al generar link"); }
  };

  const handleCopyLink = async (sale) => {
    try {
      const res = await axios.get(`${API}/pos/sales/${sale.id}/share`, { headers });
      await navigator.clipboard.writeText(res.data.public_url);
      toast.success("Link copiado");
    } catch { toast.error("Error al copiar link"); }
  };

  const DOC_BADGES = {
    prefactura: "bg-blue-50 text-blue-700 border-blue-200",
    nota_venta: "bg-blue-50 text-blue-700 border-blue-200",
    remision: "bg-blue-50 text-blue-700 border-blue-200",
    factura: "bg-blue-50 text-blue-700 border-blue-200",
  };
  const DOC_LABELS = { prefactura: "Prefactura", nota_venta: "Prefactura", remision: "Prefactura", factura: "Prefactura" };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-5" data-testid="pos-sales-history">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Receipt size={24} weight="duotone" className="text-blue-600" /> Historial de Ventas</h1>
        <p className="text-sm text-slate-500">Consulta, edita y devuelve ventas realizadas</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar folio o cliente..." className="h-9 text-sm pl-8" data-testid="pos-history-search" />
        </div>
        <select value={docFilter} onChange={e => setDocFilter(e.target.value)} className="h-9 px-3 border rounded-lg text-sm bg-white" data-testid="pos-history-filter">
          <option value="">Todos</option>
          <option value="prefactura">Prefactura</option>
        </select>
      </div>

      <div className="flex gap-4">
        <div className="bg-white border rounded-lg px-4 py-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Total Ventas</p><p className="text-xl font-extrabold text-slate-900">{filtered.length}</p></div>
        <div className="bg-white border rounded-lg px-4 py-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Monto Total</p><p className="text-xl font-extrabold text-emerald-700 font-mono">${totalAmount.toLocaleString("es-MX")}</p></div>
        <div className="bg-white border rounded-lg px-4 py-3"><p className="text-[10px] text-slate-500 uppercase font-semibold">Promedio</p><p className="text-xl font-extrabold text-blue-700 font-mono">${filtered.length ? Math.round(totalAmount / filtered.length).toLocaleString("es-MX") : 0}</p></div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b text-left">
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Folio</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Fecha</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Cliente</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Tipo</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Status</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-right">Total</th>
              <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const isReturned = s.status === "devuelta" || s.status === "parcial_devuelta";
              const rowBg = isReturned ? "bg-red-50 hover:bg-red-100/70" : "bg-green-50/40 hover:bg-green-100/50";
              const folioColor = isReturned ? "text-red-600" : "text-green-700";
              return (
              <tr key={s.id} className={`border-b ${rowBg}`}>
                <td className={`px-3 py-2 text-xs font-mono font-bold ${folioColor}`}>
                  {s.folio}
                  {isReturned && <span className="ml-1.5 text-[8px] bg-red-600 text-white px-1.5 py-0.5 rounded font-sans">{s.status === "devuelta" ? "DEVUELTA" : "PARCIAL"}</span>}
                </td>
                <td className={`px-3 py-2 text-xs ${isReturned ? "text-red-400" : "text-slate-600"}`}>{s.created_at ? format(new Date(s.created_at), "dd/MM/yy HH:mm") : ""}</td>
                <td className={`px-3 py-2 text-xs font-medium ${isReturned ? "text-red-500 line-through" : "text-slate-800"}`}>{s.customer_name}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${DOC_BADGES[s.doc_type] || "bg-slate-50 text-slate-600"}`}>{DOC_LABELS[s.doc_type] || s.doc_type}</span>
                </td>
                <td className="px-3 py-2 text-center">
                  {isReturned ? (
                    <span className="text-[9px] font-bold bg-red-600 text-white px-2 py-0.5 rounded">{s.status === "devuelta" ? "DEVUELTA" : "PARCIAL"}</span>
                  ) : s.paid_via === "mercadopago" ? (
                    <span className="text-[9px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">PAGADO MP</span>
                  ) : s.status === "pendiente" ? (
                    <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded">POR COBRAR</span>
                  ) : (
                    <span className="text-[9px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded">PAGADA</span>
                  )}
                </td>
                <td className={`px-3 py-2 text-right font-mono font-bold ${isReturned ? "text-red-600 line-through" : "text-slate-900"}`}>${(s.total || 0).toLocaleString("es-MX")}</td>
                <td className="px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1.5">
                    <button onClick={() => setViewingSale(s)} className="text-blue-500 hover:text-blue-700" title="Ver detalle" data-testid={`view-sale-${s.id}`}><Eye size={16} weight="bold" /></button>
                    <button onClick={() => handleDownloadPdf(s.id)} className="text-red-500 hover:text-red-700" title="PDF"><FilePdf size={16} /></button>
                    <button onClick={() => handleShare(s)} className="text-green-500 hover:text-green-700" title="WhatsApp"><WhatsappLogo size={16} weight="fill" /></button>
                    <button onClick={() => handleCopyLink(s)} className="text-blue-500 hover:text-blue-700" title="Copiar link"><ShareNetwork size={16} /></button>
                    {isAdmin && !isReturned && (
                      <button onClick={() => setEditingSale(s)} className="text-amber-600 hover:text-amber-800" title="Editar / Devolver" data-testid={`edit-sale-${s.id}`}>
                        <PencilSimple size={16} />
                      </button>
                    )}
                    {isAdmin && !isReturned && (
                      <button onClick={() => { if (window.confirm(`¿Eliminar venta ${s.folio}? El stock se revertira y no aparecera en el corte.`)) handleDeleteSale(s); }}
                        className="text-slate-400 hover:text-red-600" title="Eliminar" disabled={deletingSale === s.id}
                        data-testid={`delete-sale-${s.id}`}>
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Sin ventas</p>}
      </div>

      {/* Sale Detail Modal */}
      {viewingSale && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="sale-detail-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden mx-4 flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b bg-slate-50">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Receipt size={18} weight="duotone" className="text-blue-600" /> Detalle de Venta
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">Folio: <span className="font-mono font-bold text-blue-700">{viewingSale.folio}</span></p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleDownloadPdf(viewingSale.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  data-testid="sale-detail-pdf-btn">
                  <FilePdf size={14} weight="bold" /> PDF
                </button>
                <button onClick={() => setViewingSale(null)} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors" data-testid="sale-detail-close">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto p-5 space-y-4 flex-1">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">Cliente</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{viewingSale.customer_name || "Publico General"}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">Fecha</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{viewingSale.created_at ? format(new Date(viewingSale.created_at), "dd/MM/yy HH:mm") : "-"}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">Status</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5 capitalize">{viewingSale.status}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">Metodo(s) de Pago</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5 capitalize">
                    {viewingSale.paid_via === "mercadopago" ? "Mercado Pago" : (viewingSale.payments || []).map(p => p.method).join(", ") || "-"}
                  </p>
                </div>
              </div>

              <div className="border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-600 text-white">
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase">SKU</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase">Producto</th>
                      <th className="px-3 py-2 text-center text-[10px] font-semibold uppercase">Cant.</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase">P. Unit.</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewingSale.items || []).map((item, idx) => (
                      <tr key={idx} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="px-3 py-2 text-xs font-mono text-slate-600">{item.sku}</td>
                        <td className="px-3 py-2 text-xs font-medium text-slate-800">{item.name}</td>
                        <td className="px-3 py-2 text-xs text-center font-mono">{item.quantity}</td>
                        <td className="px-3 py-2 text-xs text-right font-mono">${(item.unit_price || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 text-xs text-right font-mono font-bold">${(item.line_total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <div className="w-64 space-y-1.5">
                  <div className="flex justify-between text-xs text-slate-600"><span>Subtotal:</span><span className="font-mono font-semibold">${(viewingSale.subtotal || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                  {viewingSale.global_discount_amount > 0 && (
                    <div className="flex justify-between text-xs text-red-600"><span>Descuento ({viewingSale.global_discount_pct}%):</span><span className="font-mono font-semibold">-${(viewingSale.global_discount_amount || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                  )}
                  <div className="flex justify-between text-xs text-slate-600"><span>IVA:</span><span className="font-mono font-semibold">${(viewingSale.tax_total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                  <div className="flex justify-between text-sm font-bold text-slate-900 pt-1.5 border-t"><span>Total:</span><span className="font-mono">${(viewingSale.total || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                  {viewingSale.total_paid > 0 && (
                    <div className="flex justify-between text-xs text-green-700"><span>Pagado:</span><span className="font-mono font-semibold">${(viewingSale.total_paid || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                  )}
                </div>
              </div>

              {viewingSale.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-[10px] text-amber-700 uppercase font-semibold mb-1">Notas</p>
                  <p className="text-xs text-slate-700">{viewingSale.notes}</p>
                </div>
              )}

              <p className="text-[10px] text-slate-400 text-right">Vendedor: {viewingSale.created_by_name || "-"}</p>
            </div>
          </div>
        </div>
      )}

      {/* Sale Edit / Return Modal */}
      {editingSale && (
        <SaleEditModal
          sale={editingSale}
          token={token}
          onClose={() => setEditingSale(null)}
          onSaved={() => { setEditingSale(null); fetchSales(); }}
        />
      )}
    </div>
  );
}

// =================== SALE EDIT / RETURN MODAL ===================
function SaleEditModal({ sale, token, onClose, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState((sale.items || []).map(i => ({ ...i, return_qty: 0 })));
  const [reason, setReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [prevReturns, setPrevReturns] = useState([]);

  useEffect(() => {
    // Load previous returns for this sale
    axios.get(`${API}/pos/returns`, { headers }).then(r => {
      const related = (r.data || []).filter(ret => ret.sale_id === sale.id);
      setPrevReturns(related);
      // Calculate already returned quantities
      const returned = {};
      for (const ret of related) {
        for (const ri of (ret.items || [])) {
          const key = ri.sku || ri.product_id || ri.name;
          returned[key] = (returned[key] || 0) + (ri.return_qty || 0);
        }
      }
      setItems(prev => prev.map(i => {
        const key = i.sku || i.product_id || i.name;
        return { ...i, already_returned: returned[key] || 0, return_qty: 0 };
      }));
    }).catch(() => {});
  }, [sale.id]);

  const hasReturns = items.some(i => i.return_qty > 0);
  const isFullReturn = items.every(i => {
    const max = (i.quantity || 0) - (i.already_returned || 0);
    return i.return_qty >= max;
  });
  const returnTotal = items.reduce((s, i) => s + (i.return_qty * (i.unit_price || 0)), 0);

  const setReturnAll = () => {
    setItems(items.map(i => ({
      ...i,
      return_qty: Math.max(0, (i.quantity || 0) - (i.already_returned || 0)),
    })));
  };

  const handleReturn = async () => {
    if (!hasReturns) { toast.error("Selecciona items a devolver"); return; }
    setProcessing(true);
    try {
      await axios.post(`${API}/pos/returns`, {
        sale_id: sale.id,
        items: items.filter(i => i.return_qty > 0).map(i => ({
          product_id: i.product_id, sku: i.sku, name: i.name,
          item_type: i.item_type, original_qty: i.quantity,
          return_qty: i.return_qty, unit_price: i.unit_price,
        })),
        reason,
      }, { headers });
      toast.success("Devolucion procesada - inventario actualizado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al procesar devolucion"); }
    setProcessing(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="sale-edit-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Editar Venta - {sale.folio}</h2>
            <p className="text-xs text-slate-500">{sale.customer_name} | {(sale.created_at || "").slice(0, 10)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
        </div>

        {/* Previous returns warning */}
        {prevReturns.length > 0 && (
          <div className="mx-6 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-800">
              <p className="font-semibold">Esta venta tiene {prevReturns.length} devolucion(es) previas</p>
              {prevReturns.map(r => (
                <p key={r.id} className="text-[10px] text-amber-600">{r.folio}: {(r.items || []).map(i => `${i.name} x${i.return_qty}`).join(", ")}</p>
              ))}
            </div>
          </div>
        )}

        {/* Items */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-600 uppercase">Productos de la venta</h3>
            <button onClick={setReturnAll} className="text-xs text-red-600 hover:text-red-800 font-medium" data-testid="return-all-btn">
              Devolver todo
            </button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-2 text-left text-xs font-semibold text-slate-500">Producto</th>
                <th className="pb-2 text-center text-xs font-semibold text-slate-500 w-16">Cant.</th>
                <th className="pb-2 text-center text-xs font-semibold text-slate-500 w-16">Devuelto</th>
                <th className="pb-2 text-center text-xs font-semibold text-slate-500 w-28">Devolver</th>
                <th className="pb-2 text-right text-xs font-semibold text-slate-500 w-24">P. Unit.</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const maxReturnable = Math.max(0, (item.quantity || 0) - (item.already_returned || 0));
                return (
                  <tr key={idx} className="border-b">
                    <td className="py-2.5">
                      <p className="text-xs font-semibold text-slate-800">{item.name}</p>
                      <p className="text-[10px] text-slate-400 font-mono">{item.sku}</p>
                    </td>
                    <td className="py-2.5 text-center text-xs font-mono">{item.quantity}</td>
                    <td className="py-2.5 text-center text-xs font-mono text-red-500">{item.already_returned || 0}</td>
                    <td className="py-2.5 text-center">
                      {maxReturnable > 0 ? (
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => setItems(items.map((i, j) => j === idx ? { ...i, return_qty: Math.max(0, i.return_qty - 1) } : i))}
                            className="w-6 h-6 rounded bg-slate-200 hover:bg-slate-300 flex items-center justify-center"><Minus size={10} /></button>
                          <input type="number" value={item.return_qty} min={0} max={maxReturnable}
                            onChange={e => setItems(items.map((i, j) => j === idx ? { ...i, return_qty: Math.min(maxReturnable, Math.max(0, parseInt(e.target.value) || 0)) } : i))}
                            className="w-12 h-6 text-center text-xs font-mono border rounded" />
                          <button onClick={() => setItems(items.map((i, j) => j === idx ? { ...i, return_qty: maxReturnable } : i))}
                            className="text-[9px] text-red-600 hover:text-red-800 font-medium px-1">Max</button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-400">Devuelto completo</span>
                      )}
                    </td>
                    <td className="py-2.5 text-right text-xs font-mono">${(item.unit_price || 0).toLocaleString("es-MX")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Reason + Actions */}
        <div className="px-6 pb-6 space-y-3">
          {hasReturns && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs font-bold text-red-800 mb-1">
                Devolucion {isFullReturn ? "TOTAL" : "PARCIAL"}: ${returnTotal.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
              </p>
              <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Motivo de la devolucion..." className="text-sm min-h-[50px] mt-2 bg-white" data-testid="return-reason" />
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button variant="outline" onClick={onClose} className="h-9 text-xs">Cerrar</Button>
            {hasReturns && (
              <Button className="h-9 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={handleReturn} disabled={processing} data-testid="process-return-btn">
                {processing ? "Procesando..." : `Procesar Devolucion (${isFullReturn ? "Total" : "Parcial"})`}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
